<div class="issue-item"
     [class]="'issue-item-' + issue().id">
  <form [formGroup]="form">
    <div class="control-container">
      <p-checkbox [binary]="true"
                  [ngModel]="isChecked()"
                  (ngModelChange)="onCheckboxChecked($event)"
                  [ngModelOptions]="{standalone: true}"
                  data-testid="issue-checkbox">
      </p-checkbox>
      <div class="toggle-control"
           (click)="onToggleExpansion($event)">
        <div class="issue-text"
             [class.expanded]="isExpanded()"
             data-testid="issue-text">
          @if(getPrimaryDisplayName()) {
            <span class="primary-display-name">
              {{ getPrimaryDisplayName() }}:
            </span>
          }
          <span>
            {{issue().text}}
          </span>
        </div>
        <i [class]="isExpanded() ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
           class="chevron-icon"
           data-testid="chevron-icon"
           aria-hidden="true"></i>
      </div>
    </div>
    <div class="body-container">
      @if(isCollapsed() === false || isExpanded()) {
        @if(isExpanded()) {
          <div class="button-container">
            <p-button class="edit-text-button"
                      [link]="true"
                      label="Edit Text"
                      (click)="onEditText()"
                      icon="pi pi-pen-to-square" />
            @if(this.issue().isAreaUnassigned) {
              <p-button class="assign-room-button"
                        [link]="true"
                        label="Assign Room"
                        (click)="onAssignRoom()"
                        icon="pi pi-home" />
            }
          </div>
          <app-photo-grid [galleryItems]="galleryItems()"
                          [annotateImage]="annotateImage"
                          [allowDeleteLastImage]="issue().buildQualityStep === 1"
                          (filesSelected)="onPhotosSelected($event)"
                          (imageDeleted)="onImageDeleted($event)"
                          data-testid="photo-grid">
          </app-photo-grid>
          <app-form-field label="Vendor:"
                          class="vendor-field"
                          [invalid]="isInvalid('vendor')">
            <div class="vendor-container">
              @for(vendor of assignedVendors(); track vendor.id) {
                <div class="vendor-row">
                  <button class="vendor-select-button"
                          [disabled]="isSavingVendor()"
                          (click)="onVendorSelectClick(vendor)"
                          data-testid="vendor-select-button"
                          [attr.aria-label]="'Select vendor for issue. Current selection: ' + vendor.vendorDetails.name"
                          aria-haspopup="dialog"
                          [ngClass]="{ 'error': isInvalid('vendor')}">
                    <span class="vendor-text"
                          data-testid="vendor-text">
                      {{ (vendor.vendorDetails.doingBusinessAs || vendor.vendorDetails.name) | titlecase }}
                    </span>
                    <i class="pi pi-chevron-down vendor-chevron"
                       aria-hidden="true"></i>
                  </button>
                  @if (shouldShowRemoveButton()) {
                    <button class="remove-vendor-button"
                            (click)="onRemoveVendor(vendor)"
                            [disabled]="isSavingVendor()"
                            data-testid="remove-vendor-button"
                            [attr.aria-label]="'Remove vendor: ' + vendor.vendorDetails.name + ' from issue'">
                      @if(isSavingVendor()) {
                        <i class="pi pi-spin pi-spinner"
                           aria-hidden="true"></i>
                      } @else {
                        <i class="pi pi-minus-circle"
                           aria-hidden="true"></i>
                      }
                    </button>
                  }
                </div>
                @if (vendor.externalVendorId !== SELF_ASSIGNED_VENDOR.id) {
                  <app-send-to-contact [contacts]="vendor.vendorDetails.userVendorContacts"
                                       [externalVendorId]="vendor.externalVendorId"
                                       [externalFinancialCommunityId]="lot().externalFinancialCommunityId"
                                       [vendorName]="(vendor.vendorDetails.doingBusinessAs || vendor.vendorDetails.name)"
                                       [showBackButton]="false"
                                       [showAnimations]="false"
                                       [preCheckedContacts]="checkedVendorContactsMap()[vendor.externalVendorId]"
                                       (selectionChange)="onSelectionChange($event, vendor)"
                                       data-testid="assign-send-to-contact"
                                       class="vendor-contact-component">
                  </app-send-to-contact>
                }
              }

              @if (assignedVendors().length === 0) {
                <div class="vendor-row">
                  <button class="vendor-select-button placeholder"
                          (click)="onVendorSelectClick()"
                          [disabled]="isSavingVendor()"
                          data-testid="vendor-select-button"
                          [attr.aria-label]="'Select vendor for issue. Current selection: None'"
                          aria-haspopup="dialog"
                          [ngClass]="{ 'error': isInvalid('vendor')}">
                    <span class="vendor-text"
                          data-testid="vendor-text">
                      Select Vendor
                    </span>
                    <i class="pi pi-chevron-down vendor-chevron"
                       aria-hidden="true"></i>
                  </button>
                </div>
              }
            </div>
            @if (assignedVendors().length > 0) {
              <p-button class="add-vendor-button"
                        [link]="true"
                        [disabled]="isSavingVendor()"
                        label="Another Vendor"
                        (click)="onVendorSelectClick()"
                        icon="pi pi-plus-circle" />
            }
          </app-form-field>
          <app-form-field label="Due Date:"
                          class="due-date-field"
                          [class.last-form-field]="sendableVendors().length === 0"
                          for="assign-due-date"
                          [control]="form.controls.dueDate"
                          [invalid]="isInvalid('dueDate')">
            <p-datepicker id="assign-due-date"
                          formControlName="dueDate"
                          [showIcon]="true"
                          (onSelect)="onDueDateChange()"
                          icon="pi pi-calendar-clock"
                          dateFormat="mm/dd/yy"
                          placeholder="Select Date"
                          styleClass="w-full"
                          appendTo="body"
                          data-testid="assign-due-date" />
          </app-form-field>

          @if (sendableVendors().length > 0) {
            <app-form-field label="Sent To:"
                            class="sent-to-field last-form-field">
              <div class="sent-to-section"
                   data-testid="sent-to-section">
                @for (vendor of sendableVendors(); track vendor.issueVendorIdentifier) {
                  <div class="sent-to-vendor-row"
                       [attr.data-testid]="'sent-to-vendor-row-' + vendor.issueVendorIdentifier">
                    @if (sendableVendors().length > 1) {
                      <span class="sent-to-vendor-name"
                            data-testid="sent-to-vendor-name">
                        {{ vendor.vendorDetails.name| titlecase }}:
                      </span>
                    }
                    @if ((vendor.vendorDeliveryState === VendorDeliveryState.Sent || vendor.vendorDeliveryState === VendorDeliveryState.Viewed) && vendor.lastSentAt) {
                      <div class="status-message">
                        <i class="pi pi-send"></i>
                        Sent: {{ vendor.lastSentAt | date:'MM/dd/yyyy h:mm a' }}
                      </div>
                    } @else {
                      <div class="status-message">Not sent to {{ (vendor.vendorDetails.doingBusinessAs || vendor.vendorDetails.name) | titlecase }} yet.</div>
                    }

                    @if (vendor.vendorDeliveryState === VendorDeliveryState.Viewed && vendor.viewedAt) {
                      <div class="status-message">
                        <i class="pi pi-eye"></i>
                        Viewed: {{ vendor.viewedAt | date:'MM/dd/yyyy h:mm a' }}
                      </div>
                    }
                  </div>
                }
              </div>
            </app-form-field>
          }
        }
        <div class="tags-container">
          @for (tag of tags(); track tag.id) {
            @if (tag.shouldHideOnExpand === false || isExpanded() === false) {
              <span [class]="'tag ' + tag.type"
                    data-testid="'tag-' + tag.type"
                    role="status">
                @if (tag.icon) {
                  <i [class]="'pi tag-icon ' + tag.icon"
                     aria-hidden="true"></i>
                }
                {{tag.text}}
              </span>
            }
          }
        </div>
      }
      @else if(issueRequiresContact()) {
        <span class="collapsed-issue-error">
          <i class="pi tag-icon pi-exclamation-circle"
             aria-hidden="true"></i>
          Contact Required
        </span>
      }
    </div>
    @if (isExpanded()) {
      <div class="footer-container">
        <p-button severity="contrast"
                  [label]="isMarkingComplete() ? 'Saving...' : 'Mark Item as Complete'"
                  [outlined]="true"
                  [disabled]="isMarkingComplete()"
                  styleClass="bqa-primary-btn p-fullwidth-button"
                  [loading]="isMarkingComplete()"
                  (onClick)="onMarkComplete()"
                  data-testid="save-button">
        </p-button>
      </div>
    }

    <app-standard-bottom-sheet [(isOpen)]="showEditTextSheet"
                               [hasFooter]="true"
                               [preventOverlayClose]="true"
                               [leftButtonText]="'Cancel'"
                               [leftButtonDisabled]="isSavingIssue()"
                               [rightButtonText]="isSavingIssue() ? 'Saving...' : 'Save'"
                               [rightButtonDisabled]="isSavingIssue()"
                               (rightButtonClick)="onSaveEditText()"
                               (leftButtonClick)="onCancelEditText()"
                               testId="edit-text-sheet">
      <app-form-field label="Notes"
                      inputId="not-complete-notes"
                      [required]="true"
                      [control]="form.controls.notes"
                      [invalid]="isInvalid('notes')"
                      [errorMessages]="{ required: 'Notes are required.' }">
        <app-textarea inputId="not-complete-notes"
                      formControlName="notes"
                      placeholder="Add notes"
                      [showCount]="true"
                      [maxLength]="maxNoteLength"
                      [invalid]="isInvalid('notes')"
                      testId="not-complete-notes" />
      </app-form-field>
    </app-standard-bottom-sheet>
  </form>
</div>

<app-assign-room-bottom-sheet [(isOpen)]="showAssignRoomSheet"
                              [selectedIssueCount]="1"
                              (roomAssigned)="onRoomAssigned($event)">
</app-assign-room-bottom-sheet>

<app-vendor-selection-drawer [(isOpen)]="isVendorDrawerOpen"
                             [externalFinancialCommunityId]="communityId()"
                             [vendors]="vendors()"
                             [initialSelectedVendorId]="currentlyEditingVendorAssignment()?.externalVendorId ?? null"
                             [isSelfAssigned]="issue().isSelfAssigned"
                             [assignedVendorIds]="assignedVendorIds()"
                             [isSaving]="isSavingVendor()"
                             (selectionCanceled)="onVendorSelectionCanceled()"
                             (vendorSelected)="onVendorSelected($event)"
                             data-testid="vendor-selection-drawer">
</app-vendor-selection-drawer>